
import { Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { IssueDropdown } from '../IssueDropdown';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MenuItem, Select, OutlinedInput } from '@mui/material';
import { useFeathers } from '../../app/util';
import {CircularProgress} from '@mui/material';
import WandIcon from '../../images/wand-icon.png';
import { SummarizeExampleCard } from '../SummarizeExampleCard';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { stanceConfidence, stanceNeedsApproval, approveStance, deleteStance } from './review-controls';
import { sourceAfterQuoteEdit, splitQuoteContext } from './split-quote-context';

const originFromUrl = (url) => {
  if(!url) return null;
  try {
    return new URL(url).origin;
  } catch(e) {
    return null;
  }
}

// Helper function to standardize text for matching
const standardizeText = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const EditStance = ({
  issueKey,    // the key of the issue
  issueOptions, // the options for the issue dropdown
  onReassignIssue, // the function to call when the issue is reassigned
  active,
  onFocus,
  value, // { autoSummary: { isAutoSummary, text, usefulness, issue, subIssue, sentencesReferenced, sentenceIdsReferenced, summarizeExamples }, complete, createdAt, directQuote, editsMade, issuesSecondary, hasError (can take of values of 'text', 'usefulness', or 'sources'), sources [], text, textApproved, updatedAt, usefullness, _id}
  onChange,
  shouldAutoFocus,
  possibleSources,
  candidateName,
  office,      // Optional - used for better context in summarization
  summarizeExamples, // Map of summarize examples by _id
  style
}) => {
  const ref = useRef(null);
  const isOnScreen = useOnScreen(ref);
  const feathers = useFeathers();
  const [ summaryLoading, setSummaryLoading ] = useState(false);
  const [ summarizedTextStaged, setSummarizedTextStaged ] = useState(null);
  const [ sourceTooltipOpen, setSourceTooltipOpen ] = useState(false);
  const sourceSelectionRequired = useMemo(() => {
    return value?.sources?.some(source => (
      source.directQuote && (typeof source.url !== 'string' || !source.url.trim())
    )) || false;
  }, [ value?.sources ]);
  const canSummarize = useMemo(() => {
    return value?.sources?.some(source => source.directQuote) && !sourceSelectionRequired;
  }, [ value?.sources, sourceSelectionRequired ])
  const confidence = stanceConfidence(value);
  const showApprove = stanceNeedsApproval(value);


  // Process scraped content for auto-finding sources (optional - if scrapedContent prop is provided)
  const processedContent = useMemo(() => {
    // EditStance doesn't currently receive scrapedContent, but this is here for future compatibility
    return [];
  }, []);

  const uniqueSources = useMemo(() => {
    if (!value?.sources) return [];
    const uniqueHostNames = new Set(value.sources.map(s => originFromUrl(s.url)).filter(Boolean));
    return Array.from(uniqueHostNames);
  }, [value?.sources]);

  // Get the examples for this stance if it has summarizeExamples
  const stanceExamples = useMemo(() => {
    if (!active || !value?.autoSummary?.summarizeExamples || !summarizeExamples) return [];
    return value.autoSummary.summarizeExamples
      .map(id => summarizeExamples[id])
      .filter(Boolean)
      .slice(0, 5); // Show up to 5 most recent examples
  }, [active, value?.autoSummary?.summarizeExamples, summarizeExamples])

  useEffect(() => {
    if(summarizedTextStaged) {
      const staged = summarizedTextStaged;
      setSummarizedTextStaged(null)
      if(value.text && !staged.force) return;
      if(value.autoSummary && value.autoSummary.isAutoSummary) {
        const autoSummaryNew = {
          ...value.autoSummary,
          text: staged.summary,
          ...(staged.summarizeExamples ? { summarizeExamples: staged.summarizeExamples } : {}),
          summaryGeneratedBySummarizer: true,
        }
        onChange({ ...value, text: staged.summary, autoSummary: autoSummaryNew })
      } else {
        onChange({ 
          ...value, 
          text: staged.summary, 
          autoSummary: { 
            isAutoSummary: false, 
            addedByAutoSummary: false,
            summaryGeneratedBySummarizer: true,
            text: staged.summary,
            ...(staged.summarizeExamples ? { summarizeExamples: staged.summarizeExamples } : {})
          } 
        })
      }
    }
  }, [ summarizedTextStaged, value ])

  useEffect(() => {
    if(active && ref?.current && !isOnScreen) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [active]);

  useEffect(() => {
    if(value.hasError && ref?.current && !isOnScreen) {
      onFocus(null, value)
    }
  }, [ value])

  const summarizeText = async (sourcesGiven, force) => {
    setSummaryLoading(true)
    const issueTitle = issueOptions.find((issue) => issue.key === issueKey)?.title;
    try {
      const candidateSources = sourcesGiven || value.sources;
      
      // Format sources for the new unified API - each source now has its own directQuote
      const formattedSources = candidateSources
        .filter(source => source.directQuote) // Only include sources with quotes
        .map((source) => {
          const sourceTitle = source.sourceType === 'website' 
            ? 'Candidate website' 
            : (source.title || 'Article');
          
          return {
            quote: source.directQuote,
            url: source.url || '',
            sourceTitle
          };
        });
      
      const requestData = {
        mode: 'issue',
        candidateName,
        issueTitle,
        issueKey,
        sources: formattedSources,
        ...(office && { office }) // Include office if provided
      };
      
      console.log('Issue summarize request:', requestData);
      
      const { summary, summarizeExamples } = await feathers.getService('summarize').create(requestData);
      
      setSummarizedTextStaged({
        summary: (summary || ''),
        summarizeExamples: summarizeExamples, // Store examples if provided
        force,
      });
    } catch(err) {
      console.log(`profiler failed`, err)
    } finally {
      setSummaryLoading(false);
    }
  }

  const onBlurDirectQuoteOrSource = (sourcesNew) => {
    const sourcesToRef = sourcesNew || value.sources;

    // Check if any source has a direct quote
    const hasAnyQuote = sourcesToRef?.some(s => s.directQuote);
    const allQuotesHaveSources = sourcesToRef
      ?.filter(source => source.directQuote)
      .every(source => typeof source.url === 'string' && source.url.trim());
    if(hasAnyQuote && allQuotesHaveSources && !value?.text && sourcesToRef?.length > 0 && !summaryLoading) {
      summarizeText(sourcesToRef, false)
    }
  }

  return (
    <StanceContainer>
      <Wrapper onClick={(e) => onFocus(e)} active={active ? 1 : 0} style={style} ref={ref}>
      {
        active &&
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <div style={{ 'display': 'flex', gap: '0px', alignItems: 'center' }}>
            <IssueDropdown value={issueKey} options={issueOptions} onChange={(issueKeyNew) => onReassignIssue(issueKeyNew)}/>
          </div>
          <div className='spacer' style={{ width: '20px', minWidth: '20px', flex: 1, height: '1px'}} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {
              confidence === 'high' &&
              <Tooltip title='Confidence: high'>
                <Typography variant='body2' color='textSecondary' style={{ marginRight: '4px' }}>
                  High
                </Typography>
              </Tooltip>
            }
            {
              showApprove &&
              <Tooltip title='Approve stance'>
                <IconButton
                  aria-label='Approve stance'
                  size='small'
                  onClick={() => onChange(approveStance(value))}
                >
                  <CheckCircleIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            }
            <Tooltip title='Delete stance'>
              <IconButton
                aria-label='Delete stance'
                size='small'
                onClick={() => onChange(deleteStance(value))}
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      }
      <div style={{ display: 'flex'}}>
        <div style={{ width: '4px', minWidth: '4px', borderRadius: '4px', backgroundColor: '#000000', marginRight: '8px', alignSelf: 'stretch', ...(value?.hasError === 'text' ? { backgroundColor: '#FF0000' } : {}) }} />
        <TextField
          style={{ flex: 1, backgroundColor: '#00000000' }}
          sx={!active ? {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '& .MuiInputBase-root': { padding: '0px 0px 0px 2px' },
            '& .MuiInputBase-input': { padding: '0px 0px 0px 2px' }
          } : undefined}
          inputProps={{ style: { fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: 1.25,  backgroundColor: '#00000000' } }}
          InputProps={{ disableUnderline: true, style: { backgroundColor: active ? '#FFFFFF' : '#00000000' }}}
          multiline={true}
          placeholder={'Stance summary...'}
          value={value?.text || ''}
          onFocus={(e) => onFocus(e)}
          autoFocus={shouldAutoFocus}
          onChange={(e) => {
            onChange({ ...value, text: e.target.value, hasError: false })
          }}
        />
      </div>
      {
        active &&
        <div style={{ display: 'flex', marginTop: '-8px', padding: '0px 8px', gap: '12px' }}>
          <Tooltip
            title={sourceSelectionRequired ? 'Select a source' : 'Summarize using AI' + (!canSummarize ? '. Must add source(s) with direct quote(s).' : '')}
            placement='right'
            open={sourceSelectionRequired ? sourceTooltipOpen : undefined}
            onClose={() => setSourceTooltipOpen(false)}
          >
            <span
              style={{ display: 'inline-flex' }}
              onClick={() => {
                if(sourceSelectionRequired) {
                  setSourceTooltipOpen(true);
                }
              }}
            >
              <IconButton
                size='small'
                disabled={!canSummarize || summaryLoading}
                onClick={canSummarize ? () => summarizeText(value.sources, true) : undefined}
                sx={{ '&.Mui-disabled': { pointerEvents: 'none' } }}
              >
                {
                  summaryLoading &&
                  <CircularProgress
                    color='#000000'
                    size={15}
                    style={{ alignSelf: 'center' }}
                  />
                }
                {
                  !summaryLoading &&
                  <img src={WandIcon} style={{ width: '20px', height: '20px', ...((summaryLoading || !canSummarize) ? { opacity: 0.3 } : {}) }} />
                }
              </IconButton>
            </span>
          </Tooltip>
        </div>
      }
      {
        !active &&
        <CondensedView>
          {
            value.sources?.some(s => s.directQuote) &&
            <Typography variant="body2" color="textSecondary">
              {value.sources.filter(s => s.directQuote).length} quotes from {uniqueSources.length} different sources
            </Typography>
          }
        </CondensedView>
      }
      {
        active && (
          <SourceWrapper haserror={value?.hasError === 'sources' ? 1 : 0}>
            {value.sources?.map((source, idx) => (
              <SourceWithQuote 
                key={`SourceWithQuote-${idx}-${source.url}`} 
                source={source} 
                onFocus={onFocus} 
                onRemove={() => {
                  onChange({ ...value, sources: (value.sources || []).filter((_, i) => i !== idx), hasError: false })
                }}
                onChange={(updatedSource) => {
                  const newSources = [...(value.sources || [])];
                  newSources[idx] = updatedSource;
                  onChange({ ...value, sources: newSources, hasError: false });
                  onBlurDirectQuoteOrSource(newSources);
                }}
                possibleSources={possibleSources}
                processedContent={processedContent}
              />
            ))}
            <Button 
              color='primary'
              size='small'
              sx={{
                alignSelf: 'flex-start',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                '& .MuiButton-startIcon': {
                  marginRight: '4px'
                }
              }}
              fullWidth={false}
              onClick={() => {
                onChange({ ...value, sources: [...(value.sources || []), { url: '', directQuote: '' }], hasError: false })
              }}
              startIcon={<AddIcon />}
            >
              Add quote
            </Button>
          </SourceWrapper>
        )
      }
    </Wrapper>
    {
      !active && showApprove &&
      <Tooltip title='Needs review'>
        <WarningAmberIcon color='warning' sx={{ fontSize: '20px', flexShrink: 0 }} />
      </Tooltip>
    }
    {
      active && stanceExamples.length > 0 && (
        <ExamplesSidebar>
          <Typography variant='body2' style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#666' }}>
            Similar examples
          </Typography>
          {stanceExamples.map((example, idx) => (
            <SummarizeExampleCard key={example._id || idx} example={example} />
          ))}
        </ExamplesSidebar>
      )
    }
    </StanceContainer>
  );
};

const StanceContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  position: relative;
  width: calc(100%);
`;

const Wrapper = styled.div`
  display: flex;
  padding: 12px 16px;
  left: -16px;
  position: relative;
  width: calc(100% + 32px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  transition: transform 0.15s ease;
  flex: 1;

  ${({ active }) => active ? `
    background-color: #C3CEEC;
    transform: scale(1.05);
    margin: 12px 0;
  ` : `
    opacity: 0.8;
  `}
`;

const ExamplesSidebar = styled.div`
  min-width: 300px;
  max-width: 300px;
  padding: 12px;
  background-color: #F5F5F5;
  margin-right: -300px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: scroll;
`;


const SourceWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  gap: 2px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 6px;

  ${({ haserror }) => haserror ? `
    border: 1px solid #FF0000;
    border-radius: 8px;
    padding: 8px;
  ` : ''}
`;

const CondensedView = styled.div`
  padding: 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const SourceWithQuote = ({ source, onFocus, onRemove, onChange, possibleSources, processedContent }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [quote, setQuote] = useState(source.directQuote || '');
  const [quoteContext, setQuoteContext] = useState(source.quoteContext || '');
  const [selectedSource, setSelectedSource] = useState(source.url || '');
  const showQuoteContext = isFocused && Boolean(quoteContext);

  const handleQuoteChange = (e) => {
    const newQuote = e.target.value;
    setQuote(newQuote);
    setQuoteContext('');
    const updatedSource = sourceAfterQuoteEdit(source, newQuote);

    // Auto-find source if quote is long enough and processedContent is available
    if (newQuote.length > 10 && processedContent && processedContent.length > 0) {
      const standardizedQuote = standardizeText(newQuote);
      const bestMatch = processedContent.find(content => 
        content.standardizedText.includes(standardizedQuote)
      );
      
      if (bestMatch) {
        // Get the base URL for dropdown matching
        const bestMatchUrl = new URL(bestMatch.url);
        const baseUrl = bestMatchUrl.origin;
        
        // Find matching source from possibleSources using base URL
        const sourceFullAsLink = possibleSources?.find(ps => {
          const psUrl = ps?.value?.url;
          return psUrl && (psUrl === baseUrl || psUrl.startsWith(baseUrl));
        })?.value;

        if (sourceFullAsLink) {
          const convertedToSource = {
            ...updatedSource,
            ...sourceFullAsLink,
            url: bestMatch.url, // Keep the specific subpage URL
            sourceType: ['website','questionnaire'].includes(sourceFullAsLink?.mediaType)
                ? sourceFullAsLink?.mediaType
                : 'other',
            directQuote: newQuote,
            quoteContext: updatedSource.quoteContext
          };
          // Set the base URL for the dropdown selection
          setSelectedSource(sourceFullAsLink?.url);
          onChange?.(convertedToSource);
          return;
        }
      }
    }

    onChange?.(updatedSource);
  };

  const handleSourceChange = (e) => {
    const sourceFullAsLink = possibleSources?.find(ps => ps?.value?.url === e.target.value)?.value;
    if(sourceFullAsLink) {
      const convertedToSource = {
        ...source,
        ...sourceFullAsLink,
        // Preserve the existing specific URL if it exists and matches the base URL
        url: source.url && source.url.startsWith(e.target.value) ? source.url : e.target.value,
        sourceType: ['website','questionnaire'].includes(sourceFullAsLink?.mediaType)
            ? sourceFullAsLink?.mediaType
            : 'other',
        directQuote: quote
      }
      setSelectedSource(e.target.value);
      onChange?.(convertedToSource);
    }
  };

  useEffect(() => {
    if(source.url) {
      // find the possible source that matches the url
      let possibleSource = possibleSources?.find(ps => ps.value.url === source.url);
      if(!possibleSource) {
        possibleSource = possibleSources?.find(ps => originFromUrl(ps.value.url) === originFromUrl(source.url));
      }
      if(possibleSource) {
        setSelectedSource(possibleSource.value.url);
      }
    }
  }, [source.url, possibleSources])

  useEffect(() => {
    setQuote(source.directQuote || '');
  }, [source.directQuote])

  useEffect(() => {
    setQuoteContext(source.quoteContext || '');
  }, [source.quoteContext])

  return (
    <SourceRow>
      <QuoteField>
        <QuoteTextField
          value={showQuoteContext ? quoteContext : quote}
          onChange={handleQuoteChange}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={() => setIsFocused(false)}
          multiline={isFocused}
          rows={isFocused ? undefined : 1}
          placeholder="Add quote..."
          variant="standard"
          size="small"
          fullWidth
          sx={showQuoteContext ? {
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            '& .MuiInputBase-input': {
              color: 'transparent',
              caretColor: '#000000'
            }
          } : undefined}
        />
        {showQuoteContext && (
          <QuoteContextDisplay aria-hidden="true">
            {splitQuoteContext(quoteContext, quote).map((part, idx) => (
              part.isDirectQuote
                ? <strong key={idx}>{part.text}</strong>
                : <React.Fragment key={idx}>{part.text}</React.Fragment>
            ))}
          </QuoteContextDisplay>
        )}
      </QuoteField>
      <SourceSelect
        value={selectedSource}
        onChange={handleSourceChange}
        size="small"
        input={<SourceInput />}
        displayEmpty
        sx={{
          fontSize: '0.875rem',
          '& .MuiSelect-select': {
            py: 0.5
          }
        }}
      >
        <MenuItem value="" disabled>
          Choose source
        </MenuItem>
        {possibleSources?.map(({ value, displayAs}, idx) => (
          <MenuItem value={value?.url} key={idx}>{displayAs}</MenuItem>
        ))}
      </SourceSelect>
      <IconButton 
        size="small" 
        onClick={onRemove} 
        sx={{ 
          ml: 0.5,
          p: 0.5,
          '& .MuiSvgIcon-root': {
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.54)'
          },
          '&:hover .MuiSvgIcon-root': {
            color: 'rgba(0, 0, 0, 0.87)'
          }
        }}
      >
        <CancelIcon />
      </IconButton>
    </SourceRow>
  );
};

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
`;

const QuoteField = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
`;

const QuoteContextDisplay = styled.div`
  min-height: 24px;
  padding: 4px 0;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const QuoteTextField = styled(TextField)`
  background-color: transparent;
  & .MuiInput-root {
    font-size: 0.875rem;
    background-color: transparent;
  }
`;

const SourceSelect = styled(Select)`
  background-color: white;
  min-width: 180px;
  max-width: 180px;
  width: 180px;
  & .MuiSelect-select {
    padding: 0px 0px 2px 0px;
    padding-right: 20px !important;
    font-size: 0.875rem;
  }
  & .MuiSelect-icon {
    right: 0;
  }
`;

const SourceInput = styled(OutlinedInput)`
  padding: 4px 0 2px 8px;
  border-color: transparent;
  border-radius: 18px;
  font-size: 0.875rem;
`;

const titleForSource = (item) => {
  if(item.sourceType === 'website') return 'Candidate website';
  if(item.sourceType === 'questionnaire') return item.title || 'Questionnaire';
  return item?.title || 'Other source';
}
function useOnScreen(ref) {

  const [isIntersecting, setIntersecting] = useState(false)

  const observer = useMemo(() => new IntersectionObserver(
    ([entry]) => setIntersecting(entry.isIntersecting)
  ), [ref])


  useEffect(() => {
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return isIntersecting
}

export { EditStance };
